/**
 * Treść strony /pogrzeb — wydzielona z pages/pogrzeb.tsx jako osobny,
 * JSON-serializable config. Strona łączy narracyjne sekcje (hero, usługi,
 * proces, kontakt) z żywym gridem produktów Shopify (FuneralProductsSection)
 * — logika pobierania/filtrowania/sortowania kolekcji zostaje w kodzie,
 * tu wydzielona jest wyłącznie statyczna treść redakcyjna.
 *
 * Znaki ' ' (twarda spacja) odpowiadają `&nbsp;` z oryginalnego JSX —
 * w configu trzeba użyć realnego znaku Unicode, bo string wstawiony przez
 * {tekst} w JSX nie dekoduje encji HTML.
 */
export interface ServiceCard {
  title: string;
  desc: string;
}

export interface ProcessStep {
  num: string;
  emoji: string;
  title: string;
  desc: string;
}

export interface ContactRow {
  iconKey: string;
  label: string;
  value: string;
  href: string;
}

const NBSP = ' ';

export const POGRZEB_CONTENT = {
  hero: {
    tagline: 'Towarzyszymy rodzinom w trudnych chwilach.',
    headingLine1: 'Wieńce i kompozycje',
    headingAccent: 'pogrzebowe',
    headingLine2: 'w Lublinie',
    body: `Pomagamy rodzinom pożegnać bliskich z${NBSP}godnością i${NBSP}szacunkiem. Bukiety, wieńce, wiązanki i${NBSP}sztuczne kompozycje - przygotujemy wszystko z${NBSP}troską i${NBSP}starannością.`,
    ctaCallLabel: 'Zadzwoń do nas',
    ctaSeeLabel: 'Zobacz kompozycje',
  },
  products: {
    eyebrow: 'Nasze kompozycje',
    heading: 'Znajdź odpowiednią kompozycję',
    body: 'Porównaj formy i kolorystykę kwiatów na pożegnanie. Skład, wymiary oraz dostępne warianty sprawdź w opisie wybranej kompozycji.',
  },
  services: {
    eyebrow: 'Zakres usług',
    heading: 'Co możemy dla Ciebie przygotować',
    body: 'Pomagamy w trudnych chwilach - szybko, z empatią i profesjonalizmem.',
    items: [
      { title: 'Bukiety pogrzebowe', desc: 'Eleganckie kompozycje z białych i kremowych kwiatów - lilii, chryzantem, gerber - gotowe na ceremonię.' },
      { title: 'Wieńce pogrzebowe', desc: 'Tradycyjne wieńce owalne lub okrągłe, z wstęgą i napisem lub bez. Dostępne w różnych rozmiarach.' },
      { title: 'Wiązanki na trumnę', desc: 'Delikatne wiązanki podkładane na trumnę. Komponowane z sezonowych kwiatów i zieleni.' },
      { title: 'Kompozycje na grób', desc: 'Kwiaty cięte lub doniczkowe na świeży grób. Możliwość dostawy bezpośrednio na cmentarz.' },
      { title: 'Sztuczne kompozycje', desc: 'Dekoracje z kwiatów sztucznych na grób. Sposób ustawienia i pielęgnacji dobierz do materiałów użytych w wybranej kompozycji.' },
      { title: 'Realizacja pilna', desc: 'Przyjmujemy zamówienia z krótkim wyprzedzeniem. Dzwoń do nas - postaramy się pomóc jak najszybciej.' },
    ] satisfies ServiceCard[],
  },
  process: {
    eyebrow: 'Jak zamówić',
    heading: 'Jak wygląda zamówienie?',
    steps: [
      { num: '01', emoji: '🌿', title: 'Wybierz kompozycję', desc: 'Wybierz bukiet, wieniec, wiązankę lub kompozycję sztuczną odpowiednią na ostatnie pożegnanie.' },
      { num: '02', emoji: '🚚', title: 'Szczegóły dostawy', desc: 'Sprawdź dostępny termin. Doręczenie na ceremonię uzgodnij wcześniej, podając dokładne miejsce oraz godzinę uroczystości.' },
      { num: '03', emoji: '💌', title: 'Treść szarfy', desc: 'Przygotuj napis i podpis. Dostępność szarfy oraz warunki jej wykonania potwierdź dla wybranej kompozycji.' },
      { num: '04', emoji: '💳', title: 'Płatność', desc: 'Zapłać szybko i bezpiecznie - kartą, BLIKiem lub przelewem.' },
    ] satisfies ProcessStep[],
  },
  advisory: {
    quote: `„W trudnych chwilach chcemy Wam pomóc - tak, żebyście musieli myśleć o${NBSP}jak najmniejszej liczbie rzeczy. Zajmiemy się kwiatami."`,
    quoteAttribution: 'Zespół Bukietowej',
    contactEyebrow: 'Skontaktuj się',
    contactHeadingLine1: 'Możemy',
    contactHeadingLine2: 'pomóc już dziś.',
    contactBody: `Zadzwoń lub odwiedź nas w kwiaciarni. Przyjmujemy zamówienia pilne - postaramy się przygotować kompozycję w${NBSP}miarę możliwości jak najszybciej.`,
    contactRows: [
      { iconKey: 'mapPin', label: 'Kwiaciarnia', value: 'al. Warszawska 23/1, Lublin', href: 'https://www.google.com/maps/dir/?api=1&destination=al.+Warszawska+23+Lublin' },
      { iconKey: 'mail', label: 'E-mail', value: 'kontakt@bukietowa.pl', href: 'mailto:kontakt@bukietowa.pl' },
      { iconKey: 'phone', label: 'Telefon', value: '+48 605 030 728', href: 'tel:+48605030728' },
    ] satisfies ContactRow[],
    ctaLabel: 'Zadzwoń teraz',
  },
};
